import React, { useState } from "react";
import axios from "axios";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link, useNavigate, useLocation } from "react-router-dom";
import IconHeader from "../../images/logo-lg.png";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "./index.scss";
import QuoteSlider from "./QuoteSlider";
import apiConfig from "./apiConfig";

// const API_URL = "https://4879xv8p80.execute-api.us-east-1.amazonaws.com/dev/createAuditor";
// const API_KEY = "2HjyMXdBkb7tMszH50aVJ5th1G85zAkn8kXIhaIk";

const quotes = [
    "Easily discover and quickly benefit from the most advanced technologies.",
    "Expert-level insights at your fingertips.",
    "Expedite planning, fieldwork and completion procedures.",
    "Elevate your expertise and confidence.",
    "AI-powered audits: Save time, increase profitability, and improve quality.",
];

function CreateAccount() {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(false);

    const validationSchema = Yup.object({
      firstName: Yup.string()
      .trim()
      .matches(/^[A-Za-z]+(?: [A-Za-z]+)*$/, "Only alphabets and single spaces allowed")
      .max(35, "First name cannot exceed 35 characters")
      .required("First name is required"),
  
    lastName: Yup.string()
      .trim()
      .matches(/^[A-Za-z]+(?: [A-Za-z]+)*$/, "Only alphabets and single spaces allowed")
      .max(35, "Last name cannot exceed 35 characters")
      .required("Last name is required"),
        email: Yup.string()
            .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, "Invalid email address")
            .required("Email is required"),
        phone: Yup.string()
            .transform(value => value.replace(/\D/g, "")) // Remove non-numeric characters before validation
            .matches(/^\d{10}$/, "Invalid phone number"), // Ensure exactly 10 digits
        agreeTerms: Yup.boolean().oneOf([true], "You must agree to the terms"),
    });
    

    const formik = useFormik({
        validateOnChange: true,
        validateOnBlur: false,
        initialValues: {
            firstName: location.state?.firstName || "",
            lastName: location.state?.lastName || "",
            email: location.state?.email || "",
            phone: location.state?.phone || "",
            agreeTerms: false,
        },
        validationSchema,
        onSubmit: async (values) => {
          const trimmedValues = {
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            email: values.email.trim().toLowerCase(),
            phone: values.phone.replace(/\D/g, ""),
          };
            try {
                setLoading(true);
                const response = await axios.post(
                    `${apiConfig.apiBaseURL}/createAuditor`,
                    {
                      firstName: trimmedValues.firstName,
                      lastName: trimmedValues.lastName,
                      email: trimmedValues.email,
                      phoneNumber: trimmedValues.phone,
                    },
                    {
                        headers: {
                            "x-api-key": apiConfig["x-api-key"],
                            "Content-Type": "application/json"
                        }
                    }
                );
                if (response.data.success) {
                    const userId = response.data.user._id;
                    localStorage.setItem("userId", userId);
                    toast.success("Account created successfully. OTP sent.");
                    navigate("/otp-verification", { state: { email: values.email, userId: userId, signUp: true, firstName: values.firstName, lastName: values.lastName, phone: values.phone } });
                } else {
                    toast.error("Signup failed. Please try again.");
                }
            } catch (error) {
                const errorMessage = error.response?.data?.message || "Signup failed. Please try again.";
                toast.error(errorMessage);
            } finally {
                setLoading(false);
            }
        },
    });

    return (
      <section className="credential-page">
        <Container fluid>
          <Row
            className="justify-content-md-center"
            style={{ minHeight: "100vh" }}
          >
            <Col lg={6} className="p-0">
              <div className="left-side d-md-flex align-items-center justify-content-center bg-light">
                <QuoteSlider quotes={quotes} />
              </div>
            </Col>
            <Col lg={6} className="right-side">
              <div className="mb-4 card-title">
                <figure className="text-center mb-5">
                  <LazyLoadImage
                    src={IconHeader}
                    alt="image"
                    className="img-fluid"
                  />
                </figure>
                <h5 className="fw-bolder text-dark">Create Your Account</h5>
              </div>

              <Form onSubmit={formik.handleSubmit} noValidate>
                <Form.Group className="mb-3" controlId="formFirstName">
                  <Form.Label>First Name*</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter first name"
                    {...formik.getFieldProps("firstName")}
                    isInvalid={
                      formik.touched.firstName && formik.errors.firstName
                    }
                    className="p-3"
                    maxLength={35}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.firstName}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="formLastName">
                  <Form.Label>Last Name*</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter last name"
                    {...formik.getFieldProps("lastName")}
                    isInvalid={
                      formik.touched.lastName && formik.errors.lastName
                    }
                    className="p-3"
                    maxLength={35}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.lastName}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="formEmail">
                  <Form.Label>Email Address*</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Enter email"
                    {...formik.getFieldProps("email")}
                    isInvalid={formik.touched.email && formik.errors.email}
                    className="p-3"
                    maxLength={55}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.email}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="formPhone">
                  <Form.Label>Phone Number (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter phone number"
                    value={formik.values.phone}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, "");

                      if (value.length > 10) {
                        value = value.slice(0, 10);
                      }
                      if (value.length > 6) {
                        value = `(${value.slice(0, 3)}) ${value.slice(
                          3,
                          6
                        )}-${value.slice(6, 10)}`;
                      } else if (value.length > 3) {
                        value = `(${value.slice(0, 3)}) ${value.slice(3)}`;
                      } else if (value.length > 0) {
                        value = `(${value}`;
                      }

                      formik.setFieldValue("phone", value);
                    }}
                    isInvalid={formik.touched.phone && formik.errors.phone}
                    className="p-3"
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.phone}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="termsCheckbox" className="mb-3">
  <Form.Check
    type="checkbox"
    label={
      <>
        I agree to{" "}
        <a href="https://auditpro.ai/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>{" "}
        and accept{" "}
        <a href="https://auditpro.ai/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
      </>
    }
    checked={formik.values.agreeTerms}
    onChange={(e) => formik.setFieldValue("agreeTerms", e.target.checked)}
    isInvalid={formik.touched.agreeTerms && formik.errors.agreeTerms}
    feedback={formik.errors.agreeTerms}
  />
</Form.Group>


                <Button
                  variant="primary"
                  className="mt-4 border-0 text-white font-16 px-5 py-3 w-100"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? "Creating Account..." : "Sign Up"}
                </Button>
              </Form>

              <Link
                to="/login"
                className="text-light text-decoration-none text-center d-block mt-4"
              >
                Already have an account?{" "}
                <span className="text-primary fw-bolder">Sign in</span>
              </Link>
            </Col>
          </Row>
          {/* <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
          /> */}
        </Container>
      </section>
    );
}

export default CreateAccount;
